import { UserAxios, AdminAxios } from '../../Assets/Axios/Axios';
import { CreateUploadUrl, DeleteUpload } from '../../Server/Mutation';

// Purpose keys → server-side S3 sub-folders. Must match the allowlists in each
// BFF's lib/s3.ts. Pass one of these as the 2nd arg to uploadFile so each asset
// lands in a tidy folder (e.g. professionals/avatar) instead of one flat bucket.
export const UPLOAD_FOLDERS = Object.freeze({
  // user-api
  PROFESSIONAL_AVATAR: 'professional-avatar',
  PROFESSIONAL_NIC: 'professional-nic',
  PROFESSIONAL_ADDRESS_PROOF: 'professional-address-proof',
  SERVICE_ICON: 'service-icon',
  SERVICE_BANNER: 'service-banner',
  SERVICE_GALLERY: 'service-gallery',
  USER_AVATAR: 'user-avatar',
  // admin-api
  CATEGORY_ICON: 'category-icon',
  COMPANY_LOGO: 'company-logo',
  COMPANY_BANNER: 'company-banner',
});

// Uploads no longer talk to S3 with AWS credentials from the browser. Instead a
// BFF mints a short-lived presigned PUT URL (secret stays server-side) and we
// stream the file bytes straight to S3 with it.
//
// Which BFF? It must be one the caller is authenticated against: user/professional
// pages go through user-api, admin pages through admin-api. Hence the factory +
// two exported sets of helpers (uploadFile / uploadFileAdmin).

function makeUploader(axiosClient, label) {
  /**
   * @param {File} file
   * @param {string} [folder] one of UPLOAD_FOLDERS; defaults to "misc" server-side
   * @returns {Promise<string>} the permanent public URL of the uploaded object
   */
  async function uploadFile(file, folder) {
    const contentType = file.type;

    const { data, errors } = await axiosClient(
      CreateUploadUrl({ contentType, folder: folder ?? null })
    );
    if (errors || !data?.createUploadUrl?.uploadUrl) {
      throw new Error(errors?.[0]?.message ?? 'Failed to get upload URL');
    }

    const { uploadUrl, fileUrl } = data.createUploadUrl;

    // The presigned URL was signed with these exact headers — they must match or
    // S3 rejects the signature. Only Content-Type is signed (no ACL: the bucket
    // enforces Bucket-owner ownership, so ACLs are disabled). Use fetch (not our
    // axios client) so the Firebase Authorization header is never sent to S3.
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed (${response.status})`);
    }

    return fileUrl;
  }

  /** Best-effort delete of a previously uploaded object. @param {string} url */
  async function deleteFile(url) {
    try {
      await axiosClient(DeleteUpload({ url }));
    } catch (error) {
      console.error(`[s3:${label}] deleteFile failed`, error);
    }
  }

  return { uploadFile, deleteFile };
}

// user / professional pages
const { uploadFile, deleteFile } = makeUploader(UserAxios, 'user');
// admin dashboard pages
const { uploadFile: uploadFileAdmin, deleteFile: deleteFileAdmin } = makeUploader(
  AdminAxios,
  'admin'
);

export { uploadFile, deleteFile, uploadFileAdmin, deleteFileAdmin };
